import * as React from "react";
import { Link } from "react-router";
import { Eyebrow, Pill, type PillTone } from "@ozpanel/ui";

// PageHeader — one header for every screen: breadcrumb, title, an optional status pill, an
// optional subtitle, and the action bar slot. Previously each screen invented its own: a bare
// <h1>, sometimes a "← All tenants" link, sometimes a count paragraph, actions floated
// wherever they landed. Consistent chrome is not cosmetic — it is how an operator knows where
// they are without reading.

export interface Crumb {
  label: string;
  to?: string;
}

export function PageHeader({
  crumbs,
  title,
  status,
  subtitle,
  actions,
}: {
  crumbs?: Crumb[];
  title: string;
  status?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <header className="mb-5 sm:mb-6">
      {crumbs && crumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="mb-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] text-muted">
          {crumbs.map((c, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span aria-hidden className="text-faint">›</span>}
              {c.to ? (
                <Link to={c.to} className="inline-flex min-h-8 items-center text-accent hover:underline pointer-coarse:min-h-10">
                  {c.label}
                </Link>
              ) : (
                <span className="min-w-0 break-all text-fg">{c.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}
      {/* Wraps instead of truncating: on a phone the title is often a domain or an address, and
          truncate cut off exactly the part that says which one. The actions drop below it. */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0 flex-[1_1_16rem]">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="min-w-0 text-[22px] leading-tight font-[680] tracking-[-0.025em] break-words sm:text-[26px]">{title}</h1>
            {status}
          </div>
          {subtitle && (
            <p className="mt-1.5 text-[14px] text-muted">{subtitle}</p>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

/**
 * StatusPill — the enumerated-state rule (V6.4) made reusable: a bounded set of visual
 * states, never free prose. `tone` is chosen by the caller from what the SERVER said, so an
 * unknown value renders neutral rather than being silently coerced to "ok".
 */
export type StatusTone = "ok" | "warn" | "bad" | "neutral" | "accent";

const pillTone: Record<StatusTone, PillTone> = { ok: "ok", warn: "warn", bad: "crit", neutral: "neutral", accent: "accent" };

export function StatusPill({ tone = "neutral", children }: { tone?: StatusTone; children: React.ReactNode }) {
  return <Pill tone={pillTone[tone]}>{children}</Pill>;
}

/**
 * RelatedLinks — V6.7d's "quick related actions from a deep item". A tenant's apps, backups
 * and usage are all one query away from its detail page, and were previously unreachable
 * from it: the operator had to go back to the nav, pick another section, and find the same
 * object again by name.
 */
export function RelatedLinks({
  links,
  inline,
}: {
  links: { label: string; to: string; hint?: string }[];
  /** Compact row for the TOP of a page. Cross-module navigation at the BOTTOM of a long
   *  scrolling page is navigation nobody finds — which is what these were. */
  inline?: boolean;
}) {
  if (links.length === 0) return null;
  if (inline) {
    return (
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 border-b border-line pb-4">
        <Eyebrow className="mr-1">Related</Eyebrow>
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            title={l.hint}
            className="rounded-md border border-line bg-surface px-2.5 py-1 text-[12.5px] transition-colors hover:border-[var(--ozp-accent-line)] hover:text-accent"
          >
            {l.label}
          </Link>
        ))}
      </nav>
    );
  }
  return (
    <section className="mt-8">
      <Eyebrow accent className="mb-2.5">Related</Eyebrow>
      <div className="flex flex-wrap gap-2">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="rounded-md border border-line px-3 py-1.5 text-sm hover:border-[var(--ozp-accent)] hover:text-[var(--ozp-accent)] " >
            {l.label}
            {l.hint && <span className="ml-1.5 text-xs text-muted">{l.hint}</span>}
          </Link>
        ))}
      </div>
    </section>
  );
}
